import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Select, Upload } from "antd";
import { toast } from "react-toastify";
import { PRIMARY, hexToRgba, STATUS } from "../../shellTokens";
import PatternPicker from "./PatternPicker";
import { useFabricPatterns } from "../../hooks/useFabricPatterns";
import StockNote from "./StockNote";
import {
    fmtBDT,
    isFilled,
    Mono,
    PRODUCT_FIELD_LABELS,
    PRODUCT_FIELDS,
    SectionCard,
} from "./orderFormShared";

const { Option } = Select;
const { Dragger } = Upload;

const MAX_FILES = 7;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

const priceFormatter = (value) =>
    value === undefined || value === null || value === ''
        ? ''
        : `৳ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const priceParser = (value) => (value || '').replace(/[৳,\s]/g, '');

const IndexChip = ({ n, tone = "muted" }) => (
    <span
        className="et-of-mono"
        style={{
            flex: "none",
            width: 20,
            height: 20,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
            background:
                tone === "active" ? "var(--activeBg)"
                    : tone === "error" ? "rgba(248,113,113,.14)"
                        : "var(--surface)",
            color:
                tone === "active" ? "var(--activeText)"
                    : tone === "error" ? STATUS.errorSoft
                        : "var(--dim)",
            border: tone === "muted" ? "1px solid var(--border)" : "none",
        }}
    >
        {n}
    </span>
);

const OrderProductForm = (props) => {
    const {
        files = [],
        loadMore,
        hasMore,
        fabricsLoading,
        fabrics = [],
        productTypes = [],
        fabricTypes = [],
        productFormations = [],
        formationsByType = {},
        // The fabric store's on-hand figures, for the readout on each line.
        // Absent (or answering null) wherever the balances are not available.
        onHandFor = () => null,
        orderForm,
        fetchFabrics,
        setFiles,
        setRemovedFiles,
        removedFiles,
        // Lines only turn red once the operator has tried to submit — decision 6.
        submitAttempted = false,
    } = props;

    const productsWatch = Form.useWatch('products', orderForm);
    const rows = productsWatch || [];

    // Patterns hang off the fabric, and each line may name a different one, so
    // they are fetched per fabric on first use and shared across the lines.
    const { patternsFor, loadPatterns, loadingFabric } = useFabricPatterns();

    // Fetched as soon as a line names a fabric, not only when the picker opens:
    // editing an order has to resolve an already-chosen pattern into its name
    // and swatch, and that needs the list in hand before anything is clicked.
    // Keyed off a joined string so a keystroke elsewhere in the line doesn't
    // re-run this.
    const fabricTypesInUse = useMemo(
        () => [...new Set((productsWatch || []).map((r) => r?.fabricType).filter(Boolean))].join(','),
        [productsWatch],
    );

    useEffect(() => {
        fabricTypesInUse.split(',').filter(Boolean).forEach((id) => loadPatterns(Number(id)));
    }, [fabricTypesInUse, loadPatterns]);

    // Only one line is open at a time: a complete line reads fine as a 44px
    // summary, so the expanded one is always the line being worked on.
    const [expanded, setExpanded] = useState(0);

    const handlePopupScroll = (e) => {
        const { target } = e;
        if (!fabricsLoading && hasMore && target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
            loadMore();
        }
    };

    const draggerProps = {
        name: "orderImage",
        accept: "image/*",
        multiple: true,
        listType: "picture",
        maxCount: MAX_FILES,
        onRemove: (file) => {
            const index = files.indexOf(file);
            const newFileList = files.slice();
            newFileList.splice(index, 1);
            setFiles(newFileList);
            if (removedFiles) {
                setRemovedFiles([...removedFiles, file]);
            }
        },
        beforeUpload(file) {
            if (file.size > MAX_FILE_SIZE) {
                toast.error('File size must be smaller than 5MB');
                return false; // Prevent upload
            }
            if (files.length >= MAX_FILES) {
                toast.error(`Up to ${MAX_FILES} images.`);
                return false;
            }
            // A multi-file drop calls this once per file within one tick, so the
            // updater has to build on the previous list — a spread of the
            // closed-over `files` would keep only the last file.
            setFiles((prev) => (prev.length >= MAX_FILES ? prev : [...prev, file]));
            return false;
        },
        files,
    };

    const linesTotal = rows.reduce(
        (acc, p) => acc + (Number(p?.quantity) || 0) * (Number(p?.price) || 0),
        0,
    );

    const nameOf = (list, id) => list.find((x) => x.id === id)?.name;

    /** The shelf a line names, for the on-hand readout's tooltip. */
    const shelfIdentity = (row) => [
        nameOf(fabricTypes, row?.fabricType),
        fabrics.find((f) => f.id === row?.fabrics)?.name,
        patternsFor(row?.fabricType).find((p) => p.id === row?.fabricPattern)?.name,
    ].filter(Boolean).join(" · ");

    const renderCollapsed = (name, row, controls) => {
        const missing = PRODUCT_FIELDS.filter((f) => !isFilled(row?.[f]));
        const invalid = submitAttempted && missing.length > 0;
        const qty = Number(row?.quantity) || 0;
        const price = Number(row?.price) || 0;
        const fabric = fabrics.find((f) => f.id === row?.fabrics);

        return (
            <div
                className={`et-of-line${invalid ? " et-of-line--invalid" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => setExpanded(name)}
                onFocus={() => setExpanded(name)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded(name);
                    }
                }}
            >
                <IndexChip n={name + 1} tone={invalid ? "error" : "muted"} />
                {invalid ? (
                    <>
                        <span style={{ font: "600 13px Outfit, sans-serif", color: STATUS.errorSoft, flex: 1, minWidth: 0 }}>
                            Incomplete line — {missing.map((f) => PRODUCT_FIELD_LABELS[f]).join(", ")} missing
                        </span>
                        <span
                            style={{
                                flex: "none",
                                font: "600 11.5px Outfit, sans-serif",
                                color: STATUS.errorSoft,
                                background: "rgba(248,113,113,.14)",
                                padding: "4px 9px",
                                borderRadius: 6,
                            }}
                        >
                            Fix
                        </span>
                    </>
                ) : (
                    <>
                        <span style={{ font: "600 13px Outfit, sans-serif", flex: "none" }}>
                            {nameOf(productTypes, row?.productType) || `Line ${name + 1}`}
                        </span>
                        <span
                            style={{
                                font: "400 12.5px Outfit, sans-serif",
                                color: "var(--muted)",
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {[fabric?.name, nameOf(fabricTypes, row?.fabricType)].filter(Boolean).join(" · ")
                                || "Nothing entered yet"}
                        </span>
                        <Mono style={{ flex: "none", fontSize: 12.5, fontWeight: 500, color: "var(--muted)" }}>
                            {`${qty} × ${fmtBDT(price)}`}
                        </Mono>
                        <Mono style={{ flex: "none", fontSize: 13.5, fontWeight: 700, width: 82, textAlign: "right" }}>
                            {fmtBDT(qty * price)}
                        </Mono>
                    </>
                )}
                <span style={{ display: "flex", gap: 5, flex: "none" }}>
                    {/* Nothing worth copying out of a line that is still missing fields. */}
                    {!invalid && (
                        <button
                            type="button"
                            className="et-of-iconbtn"
                            title="Duplicate line"
                            onClick={(e) => { e.stopPropagation(); controls.duplicate(); }}
                        >
                            ⧉
                        </button>
                    )}
                    <button
                        type="button"
                        className="et-of-iconbtn"
                        title="Remove line"
                        disabled={!controls.canRemove}
                        onClick={(e) => { e.stopPropagation(); controls.remove(); }}
                    >
                        ×
                    </button>
                </span>
            </div>
        );
    };

    const renderExpanded = (restField, name, row, controls) => {
        const subtotal = (Number(row?.quantity) || 0) * (Number(row?.price) || 0);

        return (
            <div
                style={{
                    border: "1px solid var(--primary)",
                    borderRadius: 11,
                    background: "var(--surface2)",
                    overflow: "hidden",
                    boxShadow: `0 0 0 3px ${hexToRgba(PRIMARY, .12)}`,
                }}
            >
                <div
                    style={{
                        padding: "10px 14px",
                        borderBottom: "1px solid var(--border)",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    <IndexChip n={name + 1} tone="active" />
                    <span style={{ font: "600 13px Outfit, sans-serif", flex: 1 }}>Editing line {name + 1}</span>
                    {/* Stated while the cloth is being picked, which is the only
                        moment the figure can change anyone's mind. */}
                    <StockNote
                        held={onHandFor(row?.fabricType, row?.fabrics, row?.fabricPattern)}
                        identity={shelfIdentity(row)}
                    />
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <button type="button" className="et-of-chip" onClick={controls.duplicate}>Duplicate</button>
                        <button
                            type="button"
                            className="et-of-chip"
                            disabled={!controls.canRemove}
                            onClick={controls.remove}
                        >
                            Remove
                        </button>
                        <button
                            type="button"
                            className="et-of-chip et-of-chip--primary"
                            onClick={() => setExpanded(null)}
                        >
                            Done
                        </button>
                    </span>
                </div>

                {/* Field order: type → formation → fabric type → pattern →
                    colour → qty → price, then description.

                    Pattern comes before colour because that is the order the
                    cloth is actually chosen in — a print is picked off the
                    fabric's book, and the colour is picked within it.

                    It does interrupt the keyboard run it used to sit out of:
                    the picker opens a swatch grid rather than taking a
                    keystroke, so the straight qty-into-price path is now
                    reached past it rather than through an unbroken line of
                    selects. */}
                <div className="et-of-prow" style={{ padding: 14 }}>
                    <Form.Item
                        {...restField}
                        name={[name, 'productType']}
                        label="Product type"
                        rules={[{ required: true, message: 'Please select product type!' }]}
                    >
                        <Select
                            placeholder="Select type"
                            onChange={() => {
                                orderForm.setFieldValue(['products', name, 'productFormation'], undefined);
                            }}
                        >
                            {productTypes.map(data => (
                                <Option value={data.id} key={data.id}>{data.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        {...restField}
                        name={[name, 'productFormation']}
                        label="Product formation"
                        rules={[{ required: true, message: 'Please select product formation!' }]}
                    >
                        {(() => {
                            const selectedTypeId = row?.productType;
                            const scopedFormations = selectedTypeId ? (formationsByType[selectedTypeId] || []) : [];
                            const currentFormationId = row?.productFormation;
                            const options = [...scopedFormations];
                            if (currentFormationId && !options.some((f) => f.id === currentFormationId)) {
                                const current = productFormations.find((f) => f.id === currentFormationId);
                                if (current) {
                                    options.unshift(current);
                                }
                            }

                            return (
                                <Select
                                    showSearch
                                    placeholder={selectedTypeId ? "Search formation" : "Select product type first"}
                                    optionFilterProp="children"
                                    disabled={!selectedTypeId}
                                >
                                    {options.map(data => (
                                        <Option value={data.id} key={data.id}>
                                            {data.name}{data.is_legacy ? ' (Legacy)' : ''}
                                        </Option>
                                    ))}
                                </Select>
                            );
                        })()}
                    </Form.Item>

                    <Form.Item
                        {...restField}
                        name={[name, 'fabricType']}
                        label="Fabric type"
                        rules={[{ required: true, message: 'Please select fabric type!' }]}
                    >
                        <Select placeholder="Select">
                            {fabricTypes.map(data => (
                                <Option value={data.id} key={data.id}>{data.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div className="et-of-prow__pattern">
                        <Form.Item
                            {...restField}
                            name={[name, 'fabricPattern']}
                            label="Fabric pattern"
                        >
                            {/* Optional: not every fabric has a pattern book, and
                                every line placed before this existed has none. */}
                            <PatternPicker
                                fabricTypeId={row?.fabricType || null}
                                patterns={patternsFor(row?.fabricType)}
                                loading={loadingFabric === row?.fabricType}
                                onOpen={loadPatterns}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        {...restField}
                        name={[name, 'fabrics']}
                        label="Fabric colour"
                        rules={[{ required: true, message: 'Please select fabric color!' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Search colour"
                            defaultActiveFirstOption={false}
                            filterOption={false}
                            onSearch={(value) => {
                                if (window.searchTimeout) clearTimeout(window.searchTimeout);
                                window.searchTimeout = setTimeout(() => fetchFabrics(1, value), 800);
                            }}
                            onPopupScroll={handlePopupScroll}
                        >
                            {fabrics.map(data => (
                                <Option title={data.name} value={data.id} key={data.id}>
                                    {data.name}
                                </Option>
                            ))}
                            {fabricsLoading && (
                                <Option value="loading" disabled>
                                    <div style={{ textAlign: 'center', padding: '10px' }}>Loading...</div>
                                </Option>
                            )}
                        </Select>
                    </Form.Item>

                    <div className="et-of-prow__qty">
                        <Form.Item
                            {...restField}
                            name={[name, "quantity"]}
                            label="Qty"
                            rules={[{ required: true, message: 'Please enter quantity!' }]}
                        >
                            <InputNumber min={1} controls={false} style={{ width: "100%" }} />
                        </Form.Item>
                    </div>

                    <div className="et-of-prow__price">
                        <Form.Item
                            {...restField}
                            name={[name, "price"]}
                            label="Unit price"
                            rules={[{ required: true, message: 'Please enter price!' }]}
                        >
                            <InputNumber
                                min={0}
                                controls={false}
                                placeholder="৳ 0"
                                formatter={priceFormatter}
                                parser={priceParser}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>
                    </div>

                    <div className="et-of-prow__desc">
                        <Form.Item
                            {...restField}
                            name={[name, 'productDescription']}
                            label="Description"
                        >
                            {/* Optional: the type, cut, fabric and print already
                                describe the line. */}
                            <Input placeholder="Notes for the tailor (optional)" />
                        </Form.Item>
                    </div>

                    <div className="et-of-prow__sub">
                        <div
                            style={{
                                height: 38,
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "0 11px",
                                background: subtotal > 0 ? "rgba(52,211,153,.12)" : "transparent",
                                border: subtotal > 0 ? "1px solid transparent" : "1px dashed var(--border)",
                            }}
                        >
                            <span
                                style={{
                                    font: "600 11.5px Outfit, sans-serif",
                                    color: subtotal > 0 ? STATUS.ok : "var(--muted)",
                                }}
                            >
                                Line subtotal
                            </span>
                            <Mono style={{ fontSize: 14, fontWeight: 700, color: subtotal > 0 ? STATUS.ok : "var(--dim)" }}>
                                {fmtBDT(subtotal)}
                            </Mono>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Form.List
            name="products"
            initialValue={[{
                productType: null,
                productFormation: null,
                fabrics: null,
                fabricType: null,
                productDescription: null,
            }]}
        >
            {(fields, { add, remove }) => (
                <SectionCard
                    id="et-of-section-products"
                    title="Products"
                    subtitle="At least one line required"
                    action={
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Mono style={{ fontSize: 12, fontWeight: 700, color: "var(--dim)" }}>
                                {`${fields.length} line${fields.length === 1 ? "" : "s"} · ${fmtBDT(linesTotal)}`}
                            </Mono>
                            <button
                                type="button"
                                className="et-of-chip"
                                style={{ height: 30, padding: "0 12px", fontSize: 12, background: "var(--surface2)" }}
                                onClick={() => { add(); setExpanded(fields.length); }}
                            >
                                + Add product
                            </button>
                        </div>
                    }
                >
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {fields.map(({ key, name, ...restField }) => {
                            const row = rows[name];
                            const controls = {
                                canRemove: fields.length > 1,
                                remove: () => {
                                    if (fields.length <= 1) return;
                                    remove(name);
                                    // Indices shift left past the removed line.
                                    setExpanded((cur) =>
                                        cur === null || cur === name ? null : cur > name ? cur - 1 : cur,
                                    );
                                },
                                // The same fabric in another colour is the most common
                                // second product, so a copy lands right below its source.
                                duplicate: () => {
                                    add({ ...(row || {}) }, name + 1);
                                    setExpanded(name + 1);
                                },
                            };
                            return (
                                <React.Fragment key={key}>
                                    {expanded === name
                                        ? renderExpanded(restField, name, row, controls)
                                        : renderCollapsed(name, row, controls)}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Not a Form.Item: this block sits inside Form.List, which
                        prefixes any field name with "products", so the picked
                        files never reached the form store. `files` is the list. */}
                    <div style={{ marginTop: 14 }}>
                        <Dragger {...draggerProps} fileList={files}>
                            <div className="et-of-drop">
                                <span
                                    style={{
                                        flex: "none",
                                        width: 46,
                                        height: 46,
                                        borderRadius: 12,
                                        background: "var(--surface)",
                                        border: "1px solid var(--border)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <span style={{ width: 16, height: 12, border: "1.5px solid var(--dim)", borderRadius: 2 }} />
                                </span>
                                <span style={{ flex: 1 }}>
                                    <span className="ant-upload-text" style={{ display: "block" }}>Reference images</span>
                                    <span className="ant-upload-hint" style={{ display: "block" }}>
                                        Drop up to {MAX_FILES} files, 5&nbsp;MB each. JPG or PNG.
                                    </span>
                                </span>
                                <Mono style={{ fontSize: 11.5, fontWeight: 600, color: "var(--dim)" }}>
                                    {`${files.length} / ${MAX_FILES}`}
                                </Mono>
                            </div>
                        </Dragger>
                    </div>
                </SectionCard>
            )}
        </Form.List>
    );
};

export default OrderProductForm;
